'use client';

import { cn } from '@/lib/utils';
import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

// ============================================================================
// Constants
// ============================================================================

// `hex` = vibrant "medium" hex shown in the picker swatches and stored
// on the API. `displayText` = dark AA-clean hex used at render time
// (the DS Badge variant text color). `backgroundHex` is the -light tint.
export const PRESET_COLORS = [
  {
    name: 'Blue',
    hex: '#4576ed',
    displayText: '#1e3fb8',
    backgroundHex: '#ecf1fd',
    variant: 'blue',
  },
  {
    name: 'Green',
    hex: '#008f63',
    displayText: '#006647',
    backgroundHex: '#e6faf0',
    variant: 'green',
  },
  {
    name: 'Teal',
    hex: '#0891b2',
    displayText: '#155e75',
    backgroundHex: '#cffafe',
    variant: 'teal',
  },
  {
    name: 'Purple',
    hex: '#8b4dd6',
    displayText: '#6322ad',
    backgroundHex: '#f0e8fa',
    variant: 'purple',
  },
  {
    name: 'Yellow',
    hex: '#ecc85a',
    displayText: '#3b504a',
    backgroundHex: '#faf1d8',
    variant: 'yellow',
  },
  {
    name: 'Orange',
    hex: '#ff881f',
    displayText: '#c2410c',
    backgroundHex: '#fff0e0',
    variant: 'orange',
  },
  {
    name: 'Red',
    hex: '#ed3821',
    displayText: '#b71d11',
    backgroundHex: '#fdecea',
    variant: 'red',
  },
  {
    name: 'Pink',
    hex: '#eb00eb',
    displayText: '#9a009a',
    backgroundHex: '#fde5fd',
    variant: 'pink',
  },
  {
    name: 'Gray',
    hex: '#3b504a',
    displayText: '#3b504a',
    backgroundHex: '#eaf1ee',
    variant: 'gray',
  },
] as const;

// Legacy hex values that existing DB records may still reference. They
// resolve to their AA-fixed counterpart at render time — no DB migration
// required.
const HEX_ALIASES: Record<string, (typeof PRESET_COLORS)[number]['variant']> = {
  // Previously-stored dark hexes (when picker briefly stored AA hex)
  '#1e3fb8': 'blue',
  '#006647': 'green',
  '#6322ad': 'purple',
  '#c2410c': 'orange',
  '#b71d11': 'red',
  '#9a009a': 'pink',
  '#155e75': 'teal',
  // Other historical aliases
  '#9e3dff': 'purple',
  '#00cc5f': 'green',
  '#d2dfdb': 'gray',
};

export type PresetColor = (typeof PRESET_COLORS)[number];

// Resolves an API hex color to a design system tag variant; falls back to 'gray'.
export const getTagVariant = (
  hexColor?: string | null,
): (typeof PRESET_COLORS)[number]['variant'] => {
  if (!hexColor) {
    return 'gray';
  }
  const normalized = hexColor.toLowerCase();
  return (
    PRESET_COLORS.find((c) => c.hex === normalized)?.variant ?? HEX_ALIASES[normalized] ?? 'gray'
  );
};

// Resolves a tag's color pair to render in a Badge.
//
// Precedence:
// 1. If `hexColor` matches a preset (or HEX_ALIASES entry), return the
//    preset's AA-clean `displayText` + `backgroundHex` pair. The
//    caller-provided `backgroundHexColor` is intentionally ignored here
//    so DB rows stored with the vibrant medium hex always render with
//    the dark, contrast-passing text.
// 2. Otherwise, if a custom `backgroundHexColor` is provided, pass the
//    raw `hexColor` + `backgroundHexColor` pair through unchanged.
// 3. Otherwise, fall back to the gray defaults.
export const getTagColors = (
  hexColor?: string | null,
  backgroundHexColor?: string | null,
): { text: string; background: string } => {
  const defaultColors = { text: '#3b504a', background: '#eaf1ee' };

  if (!hexColor) {
    return defaultColors;
  }

  // Resolve to a preset first (covers AA-fixed hexes + legacy aliases).
  // This runs before the raw passthrough so DB tags stored with old
  // pre-AA-fix hexes still render with the new AA-clean variant.
  const normalized = hexColor.toLowerCase();
  const preset = PRESET_COLORS.find((c) => c.hex === normalized);
  if (preset) {
    return { text: preset.displayText, background: preset.backgroundHex };
  }

  const aliasVariant = HEX_ALIASES[normalized];
  if (aliasVariant) {
    const aliasPreset = PRESET_COLORS.find((c) => c.variant === aliasVariant);
    if (aliasPreset) {
      return { text: aliasPreset.displayText, background: aliasPreset.backgroundHex };
    }
  }

  // Custom user hex — pass through as-is.
  if (backgroundHexColor) {
    return { text: hexColor, background: backgroundHexColor };
  }

  return defaultColors;
};

// ============================================================================
// Types
// ============================================================================

type ColorPickerProps = {
  /**
   * The currently selected text color (hex value)
   */
  value?: string;
  /**
   * Callback when a color is selected — receives text hex and background hex
   */
  onChange?: (color: string, backgroundColor: string) => void;
  /**
   * Additional className for the trigger button
   */
  className?: string;
  /**
   * Whether the color picker is disabled
   */
  disabled?: boolean;
};

// ============================================================================
// ColorPicker Component
// ============================================================================

export function ColorPicker({ value, onChange, className, disabled }: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleColorSelect = (preset: (typeof PRESET_COLORS)[number]) => {
    onChange?.(preset.hex, preset.backgroundHex);
    setIsOpen(false);
  };

  const selectedColor = value || PRESET_COLORS[0].hex;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={cn(
            'flex shrink-0 items-center justify-center cursor-pointer',
            'size-8 rounded-lg border border-primary-10 bg-white shadow-sm',
            'transition-all duration-200',
            'hover:border-primary-20 focus:outline-none focus:ring-0',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          disabled={disabled}
        >
          <div className="size-4 rounded-sm" style={{ backgroundColor: selectedColor }} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto rounded-xl border border-primary-5 bg-white p-2 shadow-md"
        align="start"
        sideOffset={8}
      >
        <div className="flex items-center gap-2">
          {PRESET_COLORS.map((color) => {
            // Compare via variant so legacy DB hexes (HEX_ALIASES) light up
            // the same swatch the renderer picks.
            const isSelected = getTagVariant(selectedColor) === color.variant;
            return (
              <button
                key={color.hex}
                type="button"
                onClick={() => handleColorSelect(color)}
                className={cn(
                  'flex items-center justify-center cursor-pointer',
                  'size-5 rounded-sm border',
                  'transition-all duration-150',
                  'focus:outline-none hover:border-primary-20',
                  isSelected ? 'border-highlight hover:border-highlight' : 'border-primary-10',
                )}
                title={color.name}
              >
                <div className="size-3.5 rounded-xs" style={{ backgroundColor: color.hex }} />
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ============================================================================
// ColorSwatch Component (for display only)
// ============================================================================

type ColorSwatchProps = {
  /**
   * The color to display (hex value)
   */
  color: string;
  /**
   * Size of the swatch
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Additional className
   */
  className?: string;
};

export function ColorSwatch({ color, size = 'md', className }: ColorSwatchProps) {
  const sizeClasses = {
    sm: 'size-4',
    md: 'size-5',
    lg: 'size-6',
  };

  return (
    <div
      className={cn('rounded-sm', sizeClasses[size], className)}
      style={{ backgroundColor: color }}
    />
  );
}
